/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
/* eslint-disable react/jsx-no-target-blank */
import React, { useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import { Dropdown, TextField } from '@fluentui/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { HelpButton } from '@bingads-webui-react/help-popup';
import { getCurrencyOptions } from '@bingads-webui-campaign-react/conversion-utils';


import { getStepStyles } from '@bingads-webui-campaign-react/conversion-wizard-components';
import { getSpecificGoalSettings } from '../../components/component-util';
import { getRevenueOptions, getBoostRevenueOptions } from '../../components/goal-creation-options';
import { validateDecimal, validateString } from '../../shared/validation-util';
import { LearnMore } from '../../../../conversion-wizard-components/src/learn-more/learn-more';
import { GoalCreationWizardContext } from '../../goal-creation-wizard-context';

const SpecificGoalSettingsViewUnstyled = (props) => {
  const {
    classes,
    goalObj,
    updateGoalContext,
    selectedGoalType,
    showError,
    resetShowError,
    permissions,
  } = props;

  const variantRevenueGoalTypes = ['DestinationGoal', 'EventGoal', 'ProductConversionGoal', 'InStoreTransactionGoal', 'OfflineConversionGoal', 'ClarityEventGoal'];

  const i18n = useContext(I18nContext);
  const wizardContext = useContext(GoalCreationWizardContext);
  const {
    Name,
    GoalValueType,
    CurrencyCode,
    GoalValue,
  } = goalObj;

  const currencyOptions = useMemo(() => getCurrencyOptions(i18n), [i18n]);
  const revenueOptions = useMemo(() => getRevenueOptions(i18n), [i18n]);
  const boostRevenueOptions = useMemo(() => getBoostRevenueOptions(i18n), [i18n]);
  // Name, Revenue are required.
  const [goalName, setGoalName] = useState(Name);
  const [revenue, setRevenue] = useState(_.isUndefined(GoalValueType) ? undefined : GoalValueType);
  // CurrencyCode, GoalValue are required if !NoValue
  const [isValueAvailable, setIsValueAvailable] = useState(GoalValueType === 'VariantValue' || GoalValueType === 'FixedValue');
  const [displayCurrency, setDisplayCurrency] = useState(!CurrencyCode ? 'USD' : CurrencyCode);
  const [goalValue, setGoalValue] = useState(_.isUndefined(GoalValue) || _.isNull(GoalValue) ? 1 : GoalValue); // GoalValue is 0 when editing a previously NoValue goal.

  const variantRevenueWarning = _TL_('You\'ll need to customize your UET tag tracking code to report variable revenue.');
  const clarityEventWarning = _TL_('Since this revenue type does not need extra code for configuration, we\'ll remove the revenue values that you previously set up in code-free mode.');
  const noValueWarning = _TL_('Use a conversion value if you want to track and optimize your revenue.');

  // error message control
  const [nameErrorMsg, setNameErrorMsg] = useState('');
  const [revenueErrorMsg, setRevenueErrorMsg] = useState('');
  const [valueErrorMsg, setValueErrorMsg] = useState('');

  const [clarityEventWarningMsg, setClarityEventWarningMsg] = useState('');

  const getNameErrorMessage = useCallback((value) => {
    if (_.isUndefined(value) || value.trim().length === 0 || !validateString(value)) {
      return i18n.getString(_TL_('Enter a valid goal name.'));
    }
    if (value.trim().length >= 100) {
      return i18n.getString(_TL_('Input value length must be less than 100.'));
    }
    return '';
  }, [i18n]);

  const getRevenueErrorMessage = useCallback((revenueOption) => {
    if (_.isUndefined(revenueOption)) {
      return i18n.getString(_TL_('Select an option for revenue value.'));
    }
    return '';
  }, [i18n]);

  const getValueErrorMessage = useCallback((value) => {
    if (_.isUndefined(value) || value.length === 0) {
      return i18n.getString(_TL_('Enter a goal value'));
    } else if (!validateDecimal(value)) {
      return i18n.getString(_TL_('Enter a valid goal value'));
    }
    return '';
  }, [i18n]);

  const getClarityEventWarningMessage = useCallback((revenueOption) => {
    const isClarityTagSetupAllowed = wizardContext.permissions && _.result(wizardContext.permissions.dynamic, 'IsClarityTagSetupAllowed');
    const isClarityEventDefined = isClarityTagSetupAllowed && goalObj.ClarityEventDefinitionId;
    if (isClarityEventDefined && wizardContext.goalData.GoalValueType === 'VariantValue' && revenueOption !== 'VariantValue') {
      return clarityEventWarning;
    }
    return '';
  }, [clarityEventWarning, goalObj.ClarityEventDefinitionId, wizardContext.goalData.GoalValueType, wizardContext.permissions]);

  const nameErrorRef = useRef(null);
  const revenueErrorRef = useRef(null);
  const valueErrorRef = useRef(null);
  const clarityEventWarningRef = useRef(null);

  useEffect(() => {
    if (showError) {
      setNameErrorMsg(getNameErrorMessage(goalName));
      setRevenueErrorMsg(getRevenueErrorMessage(revenue));
      setValueErrorMsg(getValueErrorMessage(goalValue));
    }
    setClarityEventWarningMsg(getClarityEventWarningMessage(revenue));

    // Set focus to the first error message if it exists
    if (!_.isEmpty(nameErrorMsg)) {
      nameErrorRef.current.focus();
    } else if (!_.isEmpty(revenueErrorMsg)) {
      revenueErrorRef.current.focus();
    } else if (!_.isEmpty(valueErrorMsg)) {
      valueErrorRef.current.focus();
    } else if (!_.isEmpty(clarityEventWarningMsg)) {
      clarityEventWarningRef.current.focus();
    }
  }, [clarityEventWarningMsg, getClarityEventWarningMessage, getNameErrorMessage, getRevenueErrorMessage, getValueErrorMessage, goalName, goalValue, nameErrorMsg, resetShowError, revenue, revenueErrorMsg, showError, valueErrorMsg]);

  const onChangeNameInput = useCallback((event, value) => {
    // Replace tabs in the goal name with spaces. Tabs are not supported and will throw GoalNameHasInvalidCharacters error on MT.
    const nameValue = value.replace(/\t/g, ' ');
    setGoalName(nameValue);

    const errorMessage = getNameErrorMessage(nameValue);
    setNameErrorMsg(errorMessage);
    updateGoalContext({ Name: _.isEmpty(errorMessage) ? nameValue.trim() : undefined });
  }, [updateGoalContext, getNameErrorMessage]);

  // validate numeric range, not empty
  const onChangeGoalValueInput = useCallback((event, value) => {
    setGoalValue(value);
    setValueErrorMsg(getValueErrorMessage(value));
    const updatedValue = parseFloat(value);
    updateGoalContext({ GoalValue: _.isNaN(updatedValue) || !validateDecimal(value) ? undefined : updatedValue });
  }, [getValueErrorMessage, updateGoalContext]);

  const onChangeRevenueSelection = useCallback((event, option) => {
    setRevenue(option.key);
    setRevenueErrorMsg('');
    const currentGoalValue = parseFloat(goalValue);
    updateGoalContext({ GoalValue: _.isNaN(currentGoalValue) || !_.isEmpty(valueErrorMsg) ? undefined : currentGoalValue, CurrencyCode: displayCurrency, GoalValueType: option.key }); // submit goal value, currecy code along with value type
    if (option.key === 'FixedValue' || option.key === 'VariantValue') {
      setIsValueAvailable(true);
    } else {
      setIsValueAvailable(false);
    }
  }, [displayCurrency, goalValue, updateGoalContext, valueErrorMsg]);

  const onChangeCurrencySelection = useCallback((event, option) => {
    setDisplayCurrency(option.key);
    updateGoalContext({ CurrencyCode: option.key });
  }, [updateGoalContext]);

  const onUpdateGoalProps = useCallback((updatedProp) => {
    updateGoalContext(updatedProp);
  }, [updateGoalContext]);

  const isClaritySetupEnabled = permissions && _.result(permissions.dynamic, 'IsClarityTagSetupAllowed');
  const SpecificSettingView = getSpecificGoalSettings(selectedGoalType, isClaritySetupEnabled);
  const isBoostLTVEnabled = permissions && _.result(permissions, 'IsAmplifyEnabled') && _.result(permissions.dynamic, 'BoostLTV');

  const revenueDropdownOptions = useMemo(() => {
    const options = [];
    _.each(revenueOptions, (item) => {
      if (item.key !== 'VariantValue') {
        options.push(item);
      } else if (variantRevenueGoalTypes.indexOf(selectedGoalType) > -1) {
        options.push(item);
      }
    });

    if (isBoostLTVEnabled) {
      return boostRevenueOptions;
    }
    return options;
  }, [revenueOptions, isBoostLTVEnabled, variantRevenueGoalTypes, selectedGoalType, boostRevenueOptions]);

  const isCurrencyAvailable = useMemo(() => (
    variantRevenueGoalTypes.indexOf(selectedGoalType) > -1 &&
      !isBoostLTVEnabled
  ), [isBoostLTVEnabled, selectedGoalType, variantRevenueGoalTypes]);

  return (
    <div className={classNames(classes.section, 'specific-settings')}>
      <div className={classes.defaultTitle} role="heading" aria-level={2}>{i18n.getString(_TL_('Goal settings'))}</div>
      <span className={classes.nonAccordionTitle}>{`${i18n.getString(_TL_('Name'))} *`}</span>
      <div className={classes.rowContainer}>
        <TextField
          className="goal-name"
          ariaLabel={i18n.getString(_TL_('Name'))}
          value={goalName}
          onChange={onChangeNameInput}
          styles={{ fieldGroup: { width: 180 } }}
          aria-required="true"
          required
          aria-describedby="conversion-goal-name-input-error"
        />
      </div>
      <div id="conversion-goal-name-input-error" className="row" role="alert" aria-live="assertive">
        {!_.isEmpty(nameErrorMsg) &&
          <p className={classes.errorText} ref={nameErrorRef} tabIndex={0} role="alert">
            {nameErrorMsg}
          </p>}
      </div>
      <SpecificSettingView
        goalObj={goalObj}
        updateGoalProps={onUpdateGoalProps}
        showError={showError}
        resetShowError={resetShowError}
        wizardContext={wizardContext}
      />
      <div className="revenue-setting">
        {isBoostLTVEnabled ?
          (
            <div className={classes.nonAccordionTitle}>
              <span className="title">{`${i18n.getString(_TL_('LTV'))} *`}</span>
            </div>) :
          (
            <div className={classes.nonAccordionTitle}>
              <span className="title">{`${i18n.getString(_TL_('Revenue'))} *`}</span>
              <HelpButton
                aria-label={i18n.getString(_TL_('Learn more'))}
                topic="pop_BA_UET_RevenueValue"
              />
            </div>
          )
        }
        <div className={classes.rowContainer}>
          <Dropdown
            className="revenue-dropdown"
            selectedKey={revenue}
            ariaLabel={i18n.getString(_TL_('Select the revenue type for each conversion.'))}
            options={revenueDropdownOptions}
            onChange={onChangeRevenueSelection}
            placeholder={i18n.getString(_TL_('Select a value'))}
            dropdownWidth="auto"
            required
          />
        </div>
        {isValueAvailable && (
          <div className={classes.rowContainer}>
            <TextField
              className="col-m-6 goalValue-input"
              ariaLabel={i18n.getString(_TL_('Input the goal value for each conversion.'))}
              value={goalValue}
              onChange={onChangeGoalValueInput}
              styles={{ fieldGroup: { width: 180 } }}
              aria-required="true"
              {...(valueErrorMsg ? { 'aria-describedby': 'conversion-goal-value-error' } : {})}
            />
            {isCurrencyAvailable && <Dropdown
              className="col-sm-4 currency-dropdown"
              ariaLabel={i18n.getString(_TL_('Select the currency for your goal value.'))}
              selectedKey={displayCurrency}
              options={currencyOptions}
              onChange={onChangeCurrencySelection}
              dropdownWidth="auto"
              aria-describedby="conversion-goal-revenue-error"
            /> }
          </div>
        )}
        {isValueAvailable && !_.isEmpty(valueErrorMsg) && (
          <div className="row" role="alert" id="conversion-goal-value-error" aria-live="assertive">
            <p className={classes.errorText} ref={valueErrorRef} tabIndex={0} role="alert">{valueErrorMsg}</p>
          </div>
        )}
        <div id="conversion-goal-revenue-error" className="row" role="alert" aria-live="assertive">
          {!_.isEmpty(revenueErrorMsg) && <p className={classes.errorText} ref={revenueErrorRef} tabIndex={0} role="alert">{revenueErrorMsg}</p>}
        </div>
      </div>
      {!_.isEmpty(clarityEventWarningMsg) && (
        <div className="alert alert-warning" ref={clarityEventWarningRef} tabIndex={0} role="alert">
          <span className="alert-image" />
          <span role="alert" ariaLabel={i18n.getString(clarityEventWarningMsg)}>
            {i18n.getString(clarityEventWarningMsg)}
          </span>
        </div>
      )}
      {revenue === 'VariantValue' && (
      <div className="alert alert-warning">
        <span className="alert-image" />
        <span role="alert">
          <span aria-label={i18n.getString(variantRevenueWarning)}>
            {i18n.getString(variantRevenueWarning)}
          </span>
          <LearnMore
            i18n={i18n}
            topicId="app51210"
            aria-label={i18n.getString(_TL_('Learn more about this warning'))}
          />
          {'| '}
          <a
            href="https://go.microsoft.com/fwlink/?linkid=717484"
            target="_blank"
            aria-label={i18n.getString(_TL_('See examples'))}
          >
            {i18n.getString(_TL_('See examples'))}
          </a>
        </span>
      </div>
      )}
      {_.isEmpty(clarityEventWarningMsg) && revenue === 'NoValue' && (
        <div className="alert alert-warning">
          <span className="alert-image" />
          <span role="alert" aria-label={i18n.getString(noValueWarning)}>
            {i18n.getString(noValueWarning)}
          </span>
        </div>
      )}
    </div>
  );
};

SpecificGoalSettingsViewUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getStepStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  goalObj: PropTypes.shape({}).isRequired,
  updateGoalContext: PropTypes.func.isRequired,
  selectedGoalType: PropTypes.string.isRequired,
  showError: PropTypes.bool.isRequired,
  resetShowError: PropTypes.func.isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({}),
  }).isRequired,
};

export const SpecificGoalSettingsView = withDefaultStyles(SpecificGoalSettingsViewUnstyled, getStepStyles);
